import { Component, OnInit, ViewChild, SimpleChanges, OnChanges, ElementRef, AfterViewInit, Renderer2, Output, EventEmitter, Input } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ListItem } from '../models/ListItem';
import { ContactInfo } from '../models/ContactInfo';
import { ContactInfoFormState } from '../models/FormState';
import { AdminDataService } from '../services/admin-data.service';
import { ListDataService } from '../services/list-data.service';
import { ToastNotificationComponent } from '../toast-notification/toast-notification.component';
import { UserDataService } from '../services/user-data.service';
import { UserSettingsService } from '../services/user-settings.service';
import { UserRoleIdEnum } from '../models/Enums';


@Component({
    selector: 'user-settings',
    templateUrl: 'user-settings.component.html',
    styles: [],
    standalone: false
})
export class UserSettingsComponent implements OnInit, OnChanges, AfterViewInit {

    constructor(private _adminDataService: AdminDataService, private _listDataService: ListDataService, private _fb: FormBuilder, private _renderer: Renderer2, private _userdataService: UserDataService, private _userSettingsService: UserSettingsService, private _router: Router) {

    }

    @ViewChild('closeButton') closeButton;
    @ViewChild('toastNotification') toastNotificationComponent: ToastNotificationComponent;
    @ViewChild('contactModal') modal: ElementRef;

    readonly scaleOperatorRoleId = UserRoleIdEnum.ScaleOperator;
    locList: ListItem[] = [];
    officeList: ListItem[] = [];
    errorMessage: string = '';
    formState: ContactInfoFormState;
    userInfoForm: FormGroup;
    isFormChanged: boolean = false;

    toggleUseDefaultFilters() {
        const current = this.userInfoForm.get('useDefaultFilters').value;
        const newCurrent = !current;
        this.userInfoForm.get('useDefaultFilters').setValue(newCurrent);
        this.isFormChanged = true;
    }


    formSubmitted(): void {
        this.formState = {
            submitted: false,
            name: {
                invalid: false,
                errorMessage: ''
            },
            phone: {
                invalid: false,
                errorMessage: ''
            },
            fax: {
                invalid: false,
                errorMessage: ''
            }
        };
    }

    contactInfo: ContactInfo = {
        email: "",
        name: "",
        locCode: "",
        officeLocation: "",
        phone: "",
        fax: "",
        role: "",
        useDefaultFilters: false,
        accessLevel: ""
    };

    validateName() {
        let invalid = false;
        let error = "";
        if (this.formState.submitted) {
            if (!this.contactInfo.name || this.contactInfo.name.trim() === "") {
                invalid = true;
                error = "Name cannot be blank";
            }
        }
        this.formState.name.invalid = invalid;
        this.formState.name.errorMessage = error;
    }

    validatePhone() {
        let invalid = false;
        let error = "";
        if (this.formState.submitted && this.contactInfo.phone) {
            if (this.contactInfo.phone && (this.contactInfo.phone.length < 10 || this.contactInfo.phone.length > 20)) {
                invalid = true;
                error = "Phone number must be valid";
            }
        }
        this.formState.phone.invalid = invalid;
        this.formState.phone.errorMessage = error;
    }

    validateFax() {
        let invalid = false;
        let error = "";
        if (this.formState.submitted && this.contactInfo.fax) {
            if (this.contactInfo.fax && (this.contactInfo.fax.length < 10 || this.contactInfo.fax.length > 20)) {
                invalid = true;
                error = "Fax number must be valid";
            }
        }
        this.formState.fax.invalid = invalid;
        this.formState.fax.errorMessage = error;
    }

    saveContactInfo(): void {
        this.formState.submitted = true;
        this.validateName();
        this.validatePhone();
        this.validateFax();
        let formValid: boolean = !this.formState.name.invalid && !this.formState.phone.invalid && !this.formState.fax.invalid;

        if (formValid) {
            this._adminDataService.saveContactInfo(this.contactInfo).subscribe({
                next: result => {
                    if (result) {
                        this.toastNotificationComponent.showToast("Changes saved", 3000, "success");
                        this._userSettingsService.setUseDefaultFilters(this.userInfoForm.get('useDefaultFilters')?.value);
                        this._userdataService.clearUserSettingsCache();
                        this._userSettingsService.setCurrentLocCode("");
                        this.closeButton.nativeElement.click();
                        this.resetFormState();
                    } else {
                        this.toastNotificationComponent.showToast("Failed to save, please try again", 120000, "error");
                    }
                },
                error: err => this.errorMessage = err
            });
        }
    }

    resetFormState() {
        this.userInfoForm.reset();
        this.formState = {
            submitted: false,
            name: { invalid: false, errorMessage: '' },
            phone: { invalid: false, errorMessage: '' },
            fax: { invalid: false, errorMessage: '' }
        };
        this.isFormChanged = false;
    }

    async loadUserSettings(): Promise<void> {
        let userSettings = await firstValueFrom(this._userdataService.getUserSettings());
        this.contactInfo = {
            email: userSettings.userEmail,
            name: userSettings.userName,
            locCode: userSettings.defaultLocCode,
            officeLocation: userSettings.defaultOfficeLocation,
            phone: userSettings.userPhone,
            fax: userSettings.userFax,
            role: userSettings.userRole,
            useDefaultFilters: userSettings.useDefaultFilters,
            accessLevel: userSettings.userAccessLevel
        }
        this._userSettingsService.setUseDefaultFilters(this.contactInfo.useDefaultFilters);
        this.updateFormWithContactInfo();
    }

    updateFormWithContactInfo() {
        this.userInfoForm.patchValue({
            email: this.contactInfo.email,
            name: this.contactInfo.name,
            locCode: this.contactInfo.locCode,
            officeLocation: this.contactInfo.officeLocation,
            phone: this.contactInfo.phone,
            fax: this.contactInfo.fax,
            useDefaultFilters: this.contactInfo.useDefaultFilters
        });
    }

    createUserInfoForm() {
        this.userInfoForm = this._fb.group({
            email: [{ value: '', disabled: true }],
            name: [''],
            locCode: [''],
            officeLocation: [''],
            phone: [''],
            fax: [''],
            useDefaultFilters: [false]
        });

        this.userInfoForm.valueChanges.subscribe(() => {
            this.isFormChanged = this.userInfoForm.dirty;
            this.mapFormToContactInfo();
        });
    }

    mapFormToContactInfo() {
        this.contactInfo = {
            ...this.contactInfo,
            email: this.userInfoForm.get('email').value,
            name: this.userInfoForm.get('name').value,
            locCode: this.userInfoForm.get('locCode').value,
            officeLocation: this.userInfoForm.get('officeLocation').value,
            phone: this.userInfoForm.get('phone').value,
            fax: this.userInfoForm.get('fax').value,
            useDefaultFilters: this.userInfoForm.get('useDefaultFilters').value
        };
    }

    onModalClose(): void {
        this.isFormChanged = false;
        this.userInfoForm.markAsPristine();
        this.loadUserSettings();
        this.resetFormState();
    }

    openAdminConfig(): void {
        this.closeButton.nativeElement.click();
        const baseUrl = window.location.origin + window.location.pathname;
        window.open(baseUrl + '#/admin-config', '_blank');
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['contactInfo'] && !this.userInfoForm.pristine) {
            this.isFormChanged = true;
        }
    }

    ngAfterViewInit() {
        this._renderer.listen(this.modal.nativeElement, 'hidden.bs.modal', () => {
            this.onModalClose();
            this.resetFormState();
        });
    }

    ngOnInit(): void {
        if (window.location.href.includes('scout-ai-popout')) return;

        this.createUserInfoForm();
        this.loadUserSettings();
        this.formSubmitted();
        this._listDataService.getListItems("loc").subscribe({
            next: data => {
                this.locList = data;
            },
            error: err => this.errorMessage = err
        });

        this._listDataService.getListItems("officeLocation").subscribe({
            next: data => {
                this.officeList = data;
            },
            error: err => this.errorMessage = err
        });
    }


}
